import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useEffectEvent } from 'use-effect-event';
import { useLogout } from '../account/localLogout';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { UserType } from '../graphql/generated/graphql';
import useShowInvitationModal from '../organization/useShowInvitationModal';
import ExpandedPostModal from '../post/modalView/ExpandedPostModal';
import { miscToasts } from '../toasts/toasts';
import NewTopicModal from '../topic/modal/NewTopicModal';
import { UIActions } from '../ui/uiSlice';
import { createPostPath } from '../utils/url/postPath';
import TabBarMenu from './TabBarMenu';
import type { TabBarMenuItem } from './shared-types';

const batchOn = (...results: unknown[]) => {
  return () => {
    results
      .filter((result) => typeof result === 'function')
      .forEach((result) => result());
  };
};

const DesktopAppFeatures = () => {
  type TabBarMenuState = {
    isOpen: boolean;
    openTabs: TabBarMenuItem[];
    recentTabs: TabBarMenuItem[];
  };

  const [tabBarMenuState, setTabBarMenuState] = React.useState<TabBarMenuState>(
    {
      isOpen: false,
      openTabs: [],
      recentTabs: [],
    },
  );

  const user = useAppSelector((state) => state.session.user);
  const showInvitationModal = useEffectEvent(useShowInvitationModal());

  const isLoggedIn = !!user.id;
  const isUserOrAdmin =
    isLoggedIn && (user.type === UserType.User || user.type === UserType.Admin);

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (!isUserOrAdmin) return undefined;

    return batchOn(
      window.desktop?.onFeatureRequested?.('inviteTeammates', () => {
        showInvitationModal();
      }),
      window.desktop?.onFeatureRequested?.('createTopic', () => {
        dispatch(UIActions.openModal(NewTopicModal));
      }),
    );
  }, [isUserOrAdmin, dispatch]);

  const logout = useEffectEvent(useLogout());
  useEffect(() => {
    if (!isLoggedIn) return undefined;

    return batchOn(
      window.desktop?.onFeatureRequested?.('logout', () => {
        logout();
      }),
      window.desktop?.onFeatureRequested?.('createPost', () => {
        window.open(createPostPath(), '_blank', 'noopener');
      }),
      window.desktop?.onFeatureRequested?.('copyLinkToTab', () => {
        navigator.clipboard.writeText(window.location.href).then(() => {
          dispatch(UIActions.pushToast(miscToasts.linkCopied));
        });
      }),
      window.desktop?.onFeatureRequested?.(
        'setTabBarMenuData',
        (event: TabBarMenuState) => {
          setTabBarMenuState(event);
        },
      ),
      window.desktop?.onFeatureRequested?.('previewPost', (event: string) => {
        dispatch(UIActions.openModal(ExpandedPostModal, { initialId: event }));
      }),
    );
  }, [isLoggedIn, dispatch]);

  return (
    tabBarMenuState?.isOpen &&
    createPortal(
      <TabBarMenu
        openTabs={tabBarMenuState.openTabs}
        recentTabs={tabBarMenuState.recentTabs}
        onSelect={(tab) => {
          window.desktop?.focusOrRestoreTab?.(tab.id);
          window.desktop?.setIsTabBarMenuOpen?.(false);
        }}
        onClose={() => window.desktop?.setIsTabBarMenuOpen?.(false)}
      />,
      document.body,
    )
  );
};

export default DesktopAppFeatures;
